/**
 * 路由守卫（NavigationGuards）
 * @description 集成 PermissionService 的统一权限检查，并对用户中心与后台管理路由做登录控制
 * @update 2025-12-31 Admin 路由使用 Admin RBAC Store 进行细粒度权限检查
 */

import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router'
import { ElMessage, ElNotification } from 'element-plus'
import { siteConfig } from '@/config/siteConfig'
import {
  permissionService,
  type UserPermissionInfo,
} from '@/services/PermissionService'
import { useAdminRbacStore } from '@/store/admin/rbac'
import { useUserStorev2 as useUserStore } from '@/store/modules/userCenter'
import { UserRole } from '@/types/auth'
import { getSafeUserRole } from '@/utils/roleUtils'

/**
 * 设置页面标题
 */
function setPageTitle(route: RouteLocationNormalized) {
  const metaTitle = route.meta?.title
  if (metaTitle) {
    document.title = `${String(metaTitle)} - ${siteConfig.brand.name}`
  }
  else {
    document.title = `${siteConfig.brand.name} - ${siteConfig.brand.slogan}`
  }
}

/**
 * 处理权限不足的情况
 */
function handlePermissionDenied(to: RouteLocationNormalized, reason: string, fallbackAction?: string) {
  // console.warn(`[NavigationGuards] 权限不足: ${reason}, 路由: ${to.path}`)

  let message = '您没有权限访问该页面'
  let redirectRoute: RouteLocationRaw = { name: 'Error403' }

  switch (fallbackAction) {
    case 'login':
      message = '请先登录后再访问该页面'
      redirectRoute = {
        name: 'Login',
        query: { redirect: to.fullPath },
      }
      break
    case 'register':
      message = '该功能需要注册账户后使用'
      redirectRoute = {
        name: 'Register',
        query: { redirect: to.fullPath },
      }
      break
    case 'upgrade':
      message = '该功能需要升级账户权限'
      break
    case 'contact':
      message = '如需访问该功能，请联系客服开通权限'
      break
  }

  ElNotification({
    title: '访问受限',
    message,
    type: 'warning',
    duration: 3000,
  })

  return redirectRoute
}

/**
 * 获取用户权限信息
 */
function getUserPermissionInfo(): UserPermissionInfo {
  // const isDev = import.meta.env.DEV

  try {
    const userStore = useUserStore()

    const safeRole = getSafeUserRole(userStore.userInfo, userStore.isLoggedIn, !!userStore.token)

    const result: UserPermissionInfo = {
      role: safeRole,
      permissions: userStore.permissions || [],
      isLoggedIn: userStore.isLoggedIn || false,
    }

    // if (isDev) {
    //   console.log('🎯 [权限信息构建] 用户权限信息', result)
    // }

    return result
  }
  catch {
    // if (isDev) {
    //   console.error('[权限信息获取] 获取用户权限信息失败:', error)
    // }
    // else {
    //   console.warn('获取用户权限信息失败:', error)
    // }

    return {
      role: UserRole.GUEST,
      permissions: [],
      isLoggedIn: false,
    }
  }
}

/**
 * 检查是否为管理员角色（admin 或 super_admin）
 */
function isAdminRole(role: UserRole): boolean {
  return role === UserRole.ADMIN || role === UserRole.SUPER_ADMIN
}

/**
 * 设置路由守卫
 */
export function setupNavigationGuards(router: Router) {
  // console.log('[NavigationGuards] 初始化路由守卫')

  router.beforeEach(async (to, from, next) => {
    // const isDev = import.meta.env.DEV
    // const timestamp = new Date().toISOString()

    // if (isDev) {
    //   console.group(`🛡️ [NavigationGuards] ${timestamp} - 路由守卫开始`)
    //   console.log('📍 路由变化:', { from: from.fullPath, to: to.fullPath })

    //   // 特别记录根路径访问
    //   if (to.path === '/') {
    //     console.log('🏠 [根路径访问] 检测到根路径访问')
    //     console.log('  - to.name:', to.name)
    //     console.log('  - to.matched:', to.matched.map(r => ({ path: r.path, name: r.name })))
    //     console.log('  - to.redirectedFrom:', to.redirectedFrom)
    //   }
    // }

    try {
      setPageTitle(to)

      const userInfo = getUserPermissionInfo()

      // 认证相关路由（/auth/**）
      if (to.path.startsWith('/auth/')) {
        // if (isDev) {
        //   console.log('🔐 [认证路由] 访问认证相关路由:', to.path)
        // }

        if (userInfo.isLoggedIn && (to.name === 'Login' || to.name === 'Register')) {
          const redirectPath = (to.query.redirect as string) || (isAdminRole(userInfo.role) ? '/admin' : '/')

          // if (isDev) {
          //   console.log('🔄 [认证重定向] 已登录用户访问登录/注册，重定向:', redirectPath)
          //   console.groupEnd()
          // }

          next(redirectPath)
          return
        }

        // 未登录用户允许访问认证路由
        if (!userInfo.isLoggedIn) {
          next()
          return
        }
      }

      // 任务详情页未登录拦截提示
      if (to.path.startsWith('/tasks/') && to.path !== '/tasks' && !userInfo.isLoggedIn) {
        ElMessage({
          message: '请先登录以查看任务详情',
          type: 'warning',
          duration: 3000,
        })
        next({
          name: 'Login',
          query: { redirect: to.fullPath },
        })
        return
      }

      // 用户中心路由（/user/**）需要登录
      if (to.path.startsWith('/user')) {
        // if (isDev) {
        //   console.log('👤 [用户中心] 检测到用户中心访问:', to.path)
        // }

        if (!userInfo.isLoggedIn) {
          // if (isDev) {
          //   console.log('🔒 [认证重定向] 用户未登录，重定向到前台登录页')
          // }

          next({
            name: 'Login',
            query: { redirect: to.fullPath },
          })
          return
        }

        // if (isDev) {
        //   console.log('✅ [认证通过] 用户已登录，继续权限检查')
        // }

        // 实名认证检查
        if (to.meta.requiresRealName) {
          const userStore = useUserStore()
          if (!userStore.userInfo?.isVerified) {
            ElNotification({
              title: '需要实名认证',
              message: '该功能需要完成实名认证后才能使用',
              type: 'warning',
              duration: 3000,
            })
            next({
              name: 'UserSettings',
              query: { tab: 'auth', redirect: to.fullPath }, // Assuming 'auth' is the tab name for RealNameAuth
            })
            return
          }
        }
      }

      // 后台管理路由（/v2/admin/**）登录态与角色检查
      const isAdminRoute = to.path.startsWith('/admin')
      const isAdminLoginRoute = to.path === '/admin/login'

      if (isAdminLoginRoute) {
        // 已登录的管理员访问后台登录页时直接跳转仪表盘
        if (userInfo.isLoggedIn && isAdminRole(userInfo.role)) {
          // if (isDev) {
          //   console.log('🔐 [Admin] 已登录管理员访问后台登录页，重定向到 /v2/admin/dashboard')
          //   console.groupEnd()
          // }
          next('/admin/dashboard')
          return
        }

        // if (isDev) {
        //   console.log('🔐 [Admin] 访问后台登录页，允许继续')
        //   console.groupEnd()
        // }

        next()
        return
      }

      if (isAdminRoute) {
        // 后台路由要求：已登录 + 角色为 ADMIN
        if (!userInfo.isLoggedIn || !isAdminRole(userInfo.role)) {
          // if (isDev) {
          //   console.log('🔐 [Admin] 未登录或非管理员访问后台路由，重定向到后台登录页', {
          //     isLoggedIn: userInfo.isLoggedIn,
          //     role: userInfo.role,
          //   })
          //   console.groupEnd()
          // }

          next({
            name: 'AdminLogin',
            query: { redirect: to.fullPath },
          })
          return
        }

        // 🔐 Admin 专用权限检查 - 使用 Admin RBAC Store
        // 收集路由要求的权限
        const requiredAdminPermissions = new Set<string>()
        to.matched.forEach((record) => {
          const perms = record.meta?.permissions as string[] | undefined
          if (Array.isArray(perms)) {
            perms.forEach(p => requiredAdminPermissions.add(p))
          }
        })

        if (requiredAdminPermissions.size > 0) {
          const rbacStore = useAdminRbacStore()

          // 确保 RBAC Store 已加载
          if (!rbacStore.loaded) {
            await rbacStore.loadCurrentAdminRbac()
          }

          const required = Array.from(requiredAdminPermissions)
          // super_admin 通过 hasPermission 中的 admin:* 检查可访问所有页面
          const missing = required.filter(p => !rbacStore.hasPermission(p))

          if (missing.length > 0) {
            ElNotification({
              title: '权限不足',
              message: '您没有访问此页面的权限',
              type: 'warning',
              duration: 3000,
            })

            next({ name: 'AdminForbidden' })
            return
          }
        }

        // Admin 权限检查通过，放行
        next()
        return
      }

      // ========== 非 Admin 路由的权限检查 ==========
      // 基于 meta.permissions 的权限检查（优先于路径级检查）
      const requiredPermissions = new Set<string>()
      to.matched.forEach((record) => {
        const perms = record.meta?.permissions as string[] | undefined
        if (Array.isArray(perms)) {
          perms.forEach(p => requiredPermissions.add(p))
        }
      })

      if (requiredPermissions.size > 0) {
        const required = Array.from(requiredPermissions)
        const fullPermissions = permissionService.getUserFullPermissions(userInfo.role, userInfo.permissions)
        const missing = required.filter(p => !permissionService.hasPermission(p, fullPermissions))

        if (missing.length > 0) {
          // if (isDev) {
          //   console.warn('🚫 [权限失败] meta.permissions 检查未通过', {
          //     path: to.path,
          //     required,
          //     missing,
          //     userPermissions: userInfo.permissions,
          //   })
          //   console.groupEnd()
          // }

          const redirectRoute = handlePermissionDenied(to, 'missing_permission', 'upgrade')
          next(redirectRoute)
          return
        }
      }

      // 统一路径级权限检查（基于 PermissionService 配置）
      // if (isDev) {
      //   console.log('🔍 [权限检查] 开始检查路径权限:', to.path)
      // }

      const permissionResult = permissionService.checkPathPermission(to.path, userInfo)

      if (!permissionResult.allowed) {
        // if (isDev) {
        //   console.group('🚫 [权限失败] 权限检查失败详情')
        //   console.error('结果:', permissionResult)
        //   console.groupEnd()
        //   console.groupEnd()
        // }

        const redirectRoute = handlePermissionDenied(
          to,
          permissionResult.details || permissionResult.reason || 'permission_denied',
          permissionResult.fallbackAction,
        )

        next(redirectRoute)
        return
      }

      // if (isDev) {
      //   console.log('✅ [权限通过] 允许访问:', to.path)
      //   console.groupEnd()
      // }

      next()
    }
    catch {
      // if (import.meta.env.DEV) {
      //   console.group('💥 [守卫错误] 路由守卫执行异常')
      //   console.error(error)
      //   console.groupEnd()
      // }
      // else {
      //   console.error('[NavigationGuards] 路由守卫执行出错:', error)
      // }

      ElMessage({
        message: '页面加载出现错误，请刷新重试',
        type: 'error',
        duration: 3000,
      })

      next({ name: 'Error500' })
    }
  })

  // 后置守卫：开发环境输出权限统计
  router.afterEach(() => {
    // if (import.meta.env.DEV) {
    //   const userInfo = getUserPermissionInfo()
    //   const stats = permissionService.getPermissionStats(userInfo)
    //   console.log('[NavigationGuards] 路由变更完成:', to.fullPath)
    //   console.log('[NavigationGuards] 权限统计:', stats)
    // }
  })

  // console.log('[NavigationGuards] 路由守卫设置完成')
}

/**
 * 权限检查中间件工厂函数
 */
export function createPermissionMiddleware() {
  return {
    /**
     * 检查当前路由权限
     */
    checkCurrentRoute() {
      const userInfo = getUserPermissionInfo()
      const currentRoute = window.location.pathname
      return permissionService.checkPathPermission(currentRoute, userInfo)
    },

    /**
     * 检查指定路径权限
     */
    checkPath(path: string) {
      const userInfo = getUserPermissionInfo()
      return permissionService.checkPathPermission(path, userInfo)
    },

    /**
     * 创建权限检查函数
     */
    createChecker() {
      const userInfo = getUserPermissionInfo()
      return (permission: string): boolean => {
        return permissionService.hasPermission(permission, userInfo.permissions)
      }
    },
  }
}

/**
 * 默认权限中间件实例
 */
export const permissionMiddleware = createPermissionMiddleware()
